import { useState } from 'react';
import type { WorkspaceSettings } from '@forecastify/shared';
import type { ExportDomain } from '@forecastify/shared';
import { Button, Dialog, Icon, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { domainLabel } from '../exports/export-dialog';
import { SettingsCard } from './settings-shell';
import { SettingsPage } from './settings-page';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Adatok és veszélyzóna — `markup.html` 2118–2167.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE EXPORT PANEL IS NOW T-10'S SHARED PATH, and C17 built it.** The mockup's card offers a
 * format, four scope checkboxes and *"letöltési linket e-mailben küldünk"*; ours offers one button per
 * scope, because each is a different domain and the shared dialog takes one at a time. A small export
 * comes back as a download; a large one is built and emailed as an attachment, which is what that
 * sentence promises minus the link — there is no object storage, and the contract says so.
 *
 * ⚠️ **Four buttons rather than four checkboxes and one button.** The endpoint exports one domain per
 * request — a zip of four CSVs would need an archiver and a storage location, both of which are
 * dependencies rather than lines of code. Four clicks is honest; a checkbox set that silently ran four
 * requests would be one failure away from claiming success over three.
 *
 * ⚠️⚠️ **THE DELETE BUTTON RECORDS A REQUEST AND DESTROYS NOTHING**, which is the mockup's own
 * behaviour — its toast promises *"30 nap türelmi idő"*. `0022_workspace_settings.sql` carries the
 * rest: every workspace-scoped foreign key in the schema is `on delete restrict`, so purging a
 * workspace is a deliberate teardown in foreign-key order with an irreversible blast radius, and that
 * does not belong behind a settings button.
 */

export interface DataSectionProps {
  readonly settings: WorkspaceSettings;
  readonly canManage: boolean;
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onRequestDeletion: (confirmName: string) => void;
  readonly onCancelDeletion: () => void;
  /** `0197` · the second step: delete the workspace NOW, while the request is pending. */
  readonly onDeleteNow: (confirmName: string) => void;
  readonly onExport: (domain: ExportDomain) => void;
}

export function DataSection(props: DataSectionProps) {
  const t = useT();
  const { settings, canManage } = props;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  /* ⚠️ `0197` · the immediate deletion has its OWN dialog and its own typed name — a confirmation
     carried over from the request would be a confirmation given to a different question. */
  const [nowOpen, setNowOpen] = useState(false);
  const [nowTyped, setNowTyped] = useState('');

  const requested = settings.deletionRequestedAt !== null;

  return (
    /* 3 Nov 2026 · ⚠️ **the title row every other section carries** (C67's `SettingsPage`) — this
       one was cards alone, with no page heading at all. */
    <div data-screen="settings-data" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage title={t('settings.nav.data')} width="form">
        <div className="flex flex-col gap-3">
          <SettingsCard
            title={t('settings.export.title')}
            lede={t('settings.export.lede')}
            tourId="settings-export"
          >
            <div className="mt-3 flex flex-wrap gap-2">
              {(['project', 'task', 'worklog', 'member'] as const).map((domain) => (
                <Button
                  key={domain}
                  variant="outline"
                  size="sm"
                  disabled={props.busy}
                  onClick={() => {
                    props.onExport(domain);
                  }}
                >
                  <Icon name="download" size={14} />
                  {domainLabel(t, domain)}
                </Button>
              ))}
            </div>
            {/* ⚠️ **The note is GONE — item 10, second pass.** It read *"a small export
            downloads at once; a large one we assemble and email"*, which describes an internal
            branch on a row count rather than telling somebody what to do. Whichever way it goes,
            the screen says so when it happens. */}
          </SettingsCard>

          {/**
           * ⚠️ **The standing request is a BANNER above the button, not a changed button label.** Somebody
           * arriving at this screen has to learn that a deletion is pending before they read anything
           * else — and the cancel has to be the obvious thing to click.
           */}
          {requested && (
            <div className="border-destructive/40 bg-destructive/5 flex flex-wrap items-center gap-3 rounded-xl border p-4">
              <Icon name="triangle-alert" size={16} className="text-destructive shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-destructive text-[12.5px] font-medium">
                  {t('settings.delete.pending')}
                </div>
                <div className="text-muted-foreground mt-0.5 text-[11.5px]">
                  {/* ⚠️ The actor may be gone — 0022 stores the id without a foreign key, so the name
                  resolves to null. Then the date alone is what there is to say. */}
                  {settings.deletionRequestedByName === null
                    ? t('settings.delete.pendingAt', {
                        at: (settings.deletionRequestedAt ?? '').slice(0, 10),
                      })
                    : t('settings.delete.pendingBy', {
                        name: settings.deletionRequestedByName,
                        at: (settings.deletionRequestedAt ?? '').slice(0, 10),
                      })}
                </div>
              </div>
              {canManage && (
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={props.busy}
                    onClick={props.onCancelDeletion}
                  >
                    {t('settings.delete.cancel')}
                  </Button>
                  {/* ⚠️ `0197` · only here, inside the pending state: the request is the first step
                  and this is the second. A workspace nobody asked to delete has no such button. */}
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={props.busy}
                    onClick={() => {
                      setNowTyped('');
                      setNowOpen(true);
                    }}
                  >
                    {t('settings.delete.now')}
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-4">
            <h3 className="text-destructive text-[13px] font-medium">
              {t('settings.delete.title')}
            </h3>
            <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
              {/* ⚠️ The counts come with the read, so the sentence says what is at stake rather than
              "everything". The mockup's own note does the same. */}
              {t('settings.delete.note', {
                name: settings.name,
                projects: String(settings.projectCount),
                tasks: String(settings.taskCount),
              })}
            </p>

            {canManage && (
              <Button
                variant="destructive"
                size="sm"
                className="mt-3"
                disabled={requested || props.busy}
                onClick={() => {
                  setTyped('');
                  setOpen(true);
                }}
              >
                {t('settings.delete.button')}
              </Button>
            )}
          </div>

          {props.serverError !== null && (
            <p
              role="alert"
              className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
            >
              {props.serverError}
            </p>
          )}

          <Dialog
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) setTyped('');
            }}
            title={t('settings.delete.title')}
            description={t('settings.delete.dialogBody', { name: settings.name })}
            closeLabel={t('common.close')}
          >
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium">
                  {t('settings.delete.confirmLabel', { name: settings.name })}
                </span>
                <TextInput
                  value={typed}
                  onChange={(event) => {
                    setTyped(event.target.value);
                  }}
                  placeholder={settings.name}
                />
              </label>

              <div className="flex items-center gap-2">
                <div className="flex-1" />
                <Button
                  variant="outline"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                {/**
                 * ⚠️⚠️ **A FILLED DANGER BUTTON, WHICH THE KIT RESERVES** — *"strictly for irreversible
                 * financial deletions"*. This is the one control in the product that asks to remove a
                 * whole workspace, so it is the one place the loudest thing on a dialog should be the
                 * destructive action.
                 *
                 * ⚠️ **Disabled until the name matches, AND the server checks it again.** The mockup gates
                 * only here; a confirmation the server cannot verify is not a confirmation.
                 */}
                <Button
                  variant="destructive"
                  disabled={typed.trim() !== settings.name || props.busy}
                  onClick={() => {
                    props.onRequestDeletion(typed.trim());
                    setOpen(false);
                  }}
                >
                  {t('settings.delete.confirm')}
                </Button>
              </div>
            </div>
          </Dialog>

          {/**
           * `0197` · the second typed confirmation. The sentence names the counts again, because the
           * numbers the request was made against may have moved in the meantime, and this is the last
           * moment anybody can read them.
           */}
          <Dialog
            open={nowOpen}
            onOpenChange={(next) => {
              setNowOpen(next);
              if (!next) setNowTyped('');
            }}
            title={t('settings.delete.now')}
            description={t('settings.delete.nowBody', {
              name: settings.name,
              projects: String(settings.projectCount),
              tasks: String(settings.taskCount),
            })}
            closeLabel={t('common.close')}
          >
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium">
                  {t('settings.delete.confirmLabel', { name: settings.name })}
                </span>
                <TextInput
                  value={nowTyped}
                  onChange={(event) => {
                    setNowTyped(event.target.value);
                  }}
                  placeholder={settings.name}
                />
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1" />
                <Button
                  variant="outline"
                  onClick={() => {
                    setNowOpen(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="destructive"
                  disabled={nowTyped.trim() !== settings.name || props.busy}
                  onClick={() => {
                    props.onDeleteNow(nowTyped.trim());
                    setNowOpen(false);
                  }}
                >
                  {t('settings.delete.nowConfirm')}
                </Button>
              </div>
            </div>
          </Dialog>
        </div>
      </SettingsPage>
    </div>
  );
}
